<script lang="ts" setup>
import { useTranslations } from '~/composables/translations';
import { computed } from 'vue';
import { useReferentialStore } from '~/stores/referential';
import { computeProcessIcon } from '~/composables/icons';
import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';

import { NlsProcessFamilies } from '~/typings/nls/process_families';
import { NlsMaterials } from '~/typings/nls/materials';

// PROPS
interface Props {
  materials?: any[];
  printers?: any[];
  certifications?: any[];
}

const props = withDefaults(defineProps<Props>(), {
  materials: undefined,
  printers: undefined,
  certifications: undefined,
});

// NLS
const { $i18n } = useTranslations('pages/lab/showroom');
const { $i18n: $i18nMaterials } = useTranslations('materials');
const { $i18n: $i18nProcessF } = useTranslations('process_families');

// STORES
const referential = useReferentialStore();

// COMPUTED
const allProcessFamiliesIds = computed(() => Object.keys(referential.mapProcessFamilies));
const allProcessFamiliesIdsForLab = computed(() =>
  props.materials?.map((mat) => mat.processFamilyId),
);
const allMaterialFamiliesIdsForLab = computed(() => [
  ...new Set(props.materials?.map((mat) => mat.materialFamilyId)),
]);
const allMachines = computed(() => {
  const printers: any[] = [];
  props.materials?.forEach((mat) => {
    if (mat.printerId && !printers.includes(mat.printerId)) {
      printers.push(mat.printerId);
    }
  });
  return printers.map((id) => referential.mapMachines[id]);
});
const allMachinesBrands = computed(() => [
  ...new Set(
    allMachines.value
      .filter((printer) => printer && printer.manufacturer)
      .map((printer) => printer.manufacturer),
  ),
]);
</script>
<template>
  <div class="showroom-view showroom-container">
    <div class="supported-processes-section section">
      <div class="showroom-section-subtitle">
        {{ $i18n('supportedProcesses') }}
      </div>
      <div class="supported-processes grid grid-2-columns my-10px mx-25px row-gap-5px">
        <div v-for="processFamilyId in allProcessFamiliesIds" :key="processFamilyId">
          <div class="flex grid-gap-15px align-items-center">
            <vu-icon
              :data-name="referential.mapProcessFamilies[processFamilyId].name"
              :class="{ 'has-processes': allProcessFamiliesIdsForLab?.includes(processFamilyId) }"
              :icon="computeProcessIcon(referential.mapProcessFamilies[processFamilyId].name)"
            />
            <div>
              {{
                $i18nProcessF(
                  referential.mapProcessFamilies[processFamilyId].name as keyof NlsProcessFamilies,
                )
              }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="supported-material-families-section section">
      <div class="showroom-section-subtitle">
        {{ $i18n('supportedMaterialFamilies') }}
      </div>
      <div class="supported-material-families grid my-10px mx-25px row-gap-10px">
        <template v-if="allMaterialFamiliesIdsForLab.length">
          <div
            v-for="materialFamilyId in allMaterialFamiliesIdsForLab"
            :key="materialFamilyId"
            class="item flex-clamp-1"
          >
            {{
              $i18nMaterials(
                referential.mapMaterials.family[materialFamilyId] as keyof NlsMaterials,
                {},
                true,
              )
            }}
          </div>
        </template>
        <div v-else class="no-item">
          {{ $i18n('noMaterialsYet') }}
        </div>
      </div>
    </div>
    <div class="machines-brands-section section">
      <div class="showroom-section-subtitle">
        {{ $i18n('machineBrands') }}
      </div>
      <div class="machines-brands grid my-10px mx-25px row-gap-10px">
        <template v-if="allMachinesBrands.length">
          <div
            v-for="manufacturer in allMachinesBrands"
            :key="manufacturer"
            class="item flex-clamp-1"
          >
            {{ manufacturer }}
          </div>
        </template>
        <div v-else class="no-item">
          {{ $i18n('noPrinterYet') }}
        </div>
      </div>
    </div>
    <div class="showroom-section-certifications section">
      <div class="showroom-section-subtitle">
        {{ $i18n('certifications') }}
      </div>
      <div class="certifications grid my-10px mx-25px row-gap-10px">
        <template v-if="certifications?.length">
          <div
            v-for="certification in certifications"
            :key="certification"
            class="item flex-clamp-1"
          >
            {{ certification }}
          </div>
        </template>
        <div v-else class="no-item">
          {{ $i18n('noCertifications') }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;
@forward '/assets/scss/skeletons';

#lab-showroom-overview {
  overflow: auto;

  .showroom-section-subtitle {
    margin-inline: 10px;
    font-size: 14px;
  }

  .section:not(:last-child) {
    border-bottom: 1px solid var(--grey-3);
    margin-bottom: 10px;
  }

  .supported-processes {
    .vu-icon {
      font-size: 20px;
      color: var(--grey-3);

      &.has-processes {
        color: var(--blue-3);

        &:hover {
          color: var(--blue-2);
        }
      }

      @include variables.for-xs {
        font-size: initial;
        gap: 10px;
      }
    }
  }

  .certifications,
  .supported-material-families,
  .machines-brands {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    column-gap: 10px;
    align-items: center;
    .item {
      display: block;
    }
  }
}
</style>
